import type { TimePeriod } from '@carboncopyco/types';
import {
  ColorHex,
  DropdownFilter,
  Separator,
  TextLargeParagraph,
} from '@carboncopyco/ui';
import { integersFormatter } from '@carboncopyco/utils';
import { useState } from 'react';
import { ActivityLineChart, defaultTimePeriodOptions } from '../../../Charts';
import { useActivityOverviewData } from '../../useActivityOverviewData';
import { useDemoPageProps } from '../DemoPageContext';

export const ActivityOverview = () => {
  const { organizationId, demoId, workspaceId } = useDemoPageProps();
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('30');
  const {
    chartData: presentedChartData,
    nowData: presentedNowData,
    isLoading: isLoadingPresented,
    isLoadingPrevData: presentedIsLoadingPrev,
    getPercentages: getPresentedPercentages,
    getDescription: getPresentedDescription,
    isError: isErrorPresented,
  } = useActivityOverviewData({
    organizationId,
    workspaceId,
    timePeriod,
    assetId: demoId,
    resourceTypes: ['managed_presentation'],
  });
  const { values: presentedValues } = presentedNowData?.results[0] ?? {};

  const {
    chartData: visitedChartData,
    nowData: visitedNowData,
    isLoading: isLoadingVisited,
    isLoadingPrevData: visitedIsLoadingPrev,
    getPercentages: getVisitedPercentages,
    getDescription: getVisitedDescription,
    isError: isErrorVisited,
  } = useActivityOverviewData({
    organizationId,
    workspaceId,
    timePeriod,
    assetId: demoId,
    resourceTypes: ['shared_presentation'],
  });
  const { values: visitedValues } = visitedNowData?.results[0] ?? {};

  return (
    <div className="w-full flex flex-col bg-ds-accent3-100 rounded-lg border border-b-ds-natural-150 h-full">
      <div className="flex justify-between items-center h-ds-15 px-6 shrink-0 border-b border-ds-natural-150">
        <TextLargeParagraph
          label="Activity overview"
          color="text-ds-natural-600"
        />
        <DropdownFilter
          rounded={false}
          capsuleWidth={140}
          contentWidth={140}
          items={defaultTimePeriodOptions}
          onSelect={(id) => setTimePeriod(id)}
          selected={timePeriod}
          placeholder={timePeriod}
        />
      </div>
      <div className="flex grow p-6">
        <div className="flex flex-col flex-1 pr-6 max-w-[220px]">
          <ActivityLineChart
            subtitle="Demos given"
            description={getPresentedDescription('occurrences')}
            kpiNumber={
              presentedValues?.occurrences
                ? integersFormatter(presentedValues.occurrences)
                : ''
            }
            kpiRawNumber={presentedValues?.occurrences}
            kpiPercentage={getPresentedPercentages('occurrences')}
            negative={Number(getPresentedPercentages('occurrences')) < 0}
            isError={isErrorPresented}
            isEmpty={!isLoadingPresented && !presentedValues?.occurrences}
            isLoading={isLoadingPresented || presentedIsLoadingPrev}
            lineChartData={presentedChartData}
            tooltipText="The number of times this demo was presented during the specified period"
            name="Demos given"
          />
        </div>
        <Separator />
        <div className="flex flex-col flex-1 pl-6 max-w-[220px]">
          <ActivityLineChart
            subtitle="Sandbox visits"
            description={getVisitedDescription('occurrences')}
            kpiNumber={
              visitedValues?.occurrences
                ? integersFormatter(visitedValues.occurrences)
                : ''
            }
            kpiRawNumber={visitedValues?.occurrences}
            kpiPercentage={getVisitedPercentages('occurrences')}
            negative={Number(getVisitedPercentages('occurrences')) < 0}
            isError={isErrorVisited}
            isEmpty={!isLoadingPresented && !visitedValues?.occurrences}
            isLoading={isLoadingVisited || visitedIsLoadingPrev}
            lineChartData={visitedChartData}
            tooltipText="The number of times any sandbox made from this demo was visited during the specified period"
            name="Visits"
            color={ColorHex.accent1200}
          />
        </div>
      </div>
    </div>
  );
};
